import { useState } from 'react';
import { useAvisos } from '../../contextos/AvisosContext';
import { IconoCerrar } from '../../componentes/Iconos';
import { useCrearRemuneracion } from '../../lib/mutaciones';

interface ModalRegistrarRemuneracionProps {
  abierto: boolean;
  alCerrar: () => void;
  periodoMes: string;
}

const INSTITUCIONES_PREVISIONALES = [
  'FONASA',
  'AFP HABITAT',
  'AFP CUPRUM',
  'AFP PROVIDA',
  'AFP MODELO',
  'AFP PLANVITAL',
  'AFP UNO',
  'ISAPRE CONSALUD',
  'ISAPRE BANMEDICA',
  'ISAPRE COLMENA',
  'ISAPRE CRUZBLANCA',
];

export function ModalRegistrarRemuneracion({
  abierto,
  alCerrar,
  periodoMes,
}: ModalRegistrarRemuneracionProps) {
  const avisos = useAvisos();
  const crearMut = useCrearRemuneracion();

  const [nombreEmpleado, setNombreEmpleado] = useState('');
  const [rutEmpleado, setRutEmpleado] = useState('');
  const [cargo, setCargo] = useState('');
  const [prevision, setPrevision] = useState('FONASA');
  const [sueldoBase, setSueldoBase] = useState<number>(1200000);
  const [sueldoLiquido, setSueldoLiquido] = useState<number>(980000);
  const [montoPrevision, setMontoPrevision] = useState<number>(220000);
  const [bonos, setBonos] = useState<number>(0);
  const [detalleBonos, setDetalleBonos] = useState('');
  const [notas, setNotas] = useState('');

  if (!abierto) return null;

  // Autocalcular sugerencia de líquido y leyes sociales al cambiar el base
  const handleBaseChange = (base: number) => {
    setSueldoBase(base);
    const prevEst = Math.round(base * 0.19); // ~19% promedio leyes sociales en Chile
    const liqEst = base - prevEst;
    setMontoPrevision(prevEst);
    setSueldoLiquido(liqEst);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombreEmpleado.trim() || !cargo.trim()) {
      avisos.error('Por favor ingresa el nombre y cargo del colaborador.');
      return;
    }

    try {
      await crearMut.mutateAsync({
        nombreEmpleado: nombreEmpleado.trim(),
        rutEmpleado: rutEmpleado.trim() || undefined,
        cargo: cargo.trim(),
        prevision,
        sueldoBase: Number(sueldoBase),
        sueldoLiquido: Number(sueldoLiquido),
        montoPrevision: Number(montoPrevision) || undefined,
        bonos: Number(bonos) || undefined,
        detalleBonos: detalleBonos.trim() || undefined,
        periodoMes,
        estadoPago: 'PENDIENTE',
        notas: notas.trim() || undefined,
      });

      avisos.exito(`Remuneración de ${nombreEmpleado} registrada con éxito para ${periodoMes}.`);
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al registrar remuneración.');
    }
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div
        className="modal"
        style={{ maxWidth: 580, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>👤</span>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                Nueva Liquidación / Colaborador
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                Período: <strong>{periodoMes}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={alCerrar}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: 4,
            }}
            aria-label="Cerrar modal"
          >
            <IconoCerrar size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="rem_nombre">Nombre Completo del Colaborador *</label>
              <input
                id="rem_nombre"
                type="text"
                required
                placeholder="Ej: Andrea Morales Silva"
                value={nombreEmpleado}
                onChange={(e) => setNombreEmpleado(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="rem_rut">RUT del Trabajador</label>
              <input
                id="rem_rut"
                type="text"
                placeholder="Ej: 15.420.312-8"
                value={rutEmpleado}
                onChange={(e) => setRutEmpleado(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="rem_cargo">Cargo / Puesto *</label>
              <input
                id="rem_cargo"
                type="text"
                required
                placeholder="Ej: Jefa de Proyectos TI"
                value={cargo}
                onChange={(e) => setCargo(e.target.value)}
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="rem_prevision">Institución Previsional (AFP / Salud)</label>
              <select
                id="rem_prevision"
                value={prevision}
                onChange={(e) => setPrevision(e.target.value)}
              >
                {INSTITUCIONES_PREVISIONALES.map((inst) => (
                  <option key={inst} value={inst}>
                    {inst}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              padding: '14px',
              background: 'var(--bg-muted, rgba(255,255,255,0.03))',
              borderRadius: 6,
              border: '1px solid var(--border-color)',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 12,
            }}
          >
            <div className="form-group">
              <label htmlFor="rem_base">Sueldo Base (CLP) *</label>
              <input
                id="rem_base"
                type="number"
                min="0"
                step="1000"
                required
                value={sueldoBase}
                onChange={(e) => handleBaseChange(Number(e.target.value))}
              />
            </div>

            <div className="form-group">
              <label htmlFor="rem_prev">Imposiciones (CLP)</label>
              <input
                id="rem_prev"
                type="number"
                min="0"
                step="1000"
                value={montoPrevision}
                onChange={(e) => setMontoPrevision(Number(e.target.value))}
                title="Aporte previsional legal a Previred"
              />
            </div>

            <div className="form-group">
              <label htmlFor="rem_liq">Sueldo Líquido (CLP) *</label>
              <input
                id="rem_liq"
                type="number"
                min="0"
                step="1000"
                required
                value={sueldoLiquido}
                onChange={(e) => setSueldoLiquido(Number(e.target.value))}
                title="Monto que el colaborador recibe en su cuenta corriente"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <div className="form-group">
              <label htmlFor="rem_bono">Bonos Extra (CLP)</label>
              <input
                id="rem_bono"
                type="number"
                min="0"
                step="1000"
                value={bonos}
                onChange={(e) => setBonos(Number(e.target.value))}
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="rem_detbono">Detalle / Motivo del Bono</label>
              <input
                id="rem_detbono"
                type="text"
                placeholder="Ej: Bono por cumplimiento de metas"
                value={detalleBonos}
                onChange={(e) => setDetalleBonos(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="rem_notas">Notas de Auditoría Interna</label>
            <input
              id="rem_notas"
              type="text"
              placeholder="Ej: Contrato indefinido, jornada completa"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              paddingTop: 12,
              borderTop: '1px solid var(--border-color)',
            }}
          >
            <button type="button" className="btn btn-secondary" onClick={alCerrar}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={crearMut.isPending}>
              {crearMut.isPending ? 'Registrando…' : 'Guardar Liquidación'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
